import PageLoader from '@components/PageLoader'
import { useAdminIdea } from '@hooks/useAdminIdea'
import React from 'react'
import { useParams } from 'react-router-dom'
import { formatDateTime, formatName } from '../adminFormat'
import {
	BackLink,
	Card,
	CardTitle,
	Chips,
	DeletedBadge,
	DetailHeader,
	DetailTitle,
	EmptyState,
	ErrorState,
	Field,
	FieldGrid,
	FieldLabel,
	FieldValue,
	InlineLink,
	Tag,
} from '../adminDetail.styles'

/**
 * Read-only admin detail for one idea: its scope (trip-attached or personal), the
 * trip it belongs to when applicable (linked), creator (linked), location, and
 * tags. No mutations.
 */
const IdeaDetailPage: React.FC = () => {
	const { id } = useParams<{ id: string }>()
	const { data: idea, isLoading, isError } = useAdminIdea(Number(id))

	return (
		<>
			<BackLink to="/admin/ideas">← Back to ideas</BackLink>

			{isLoading ? (
				<PageLoader label="Loading idea…" />
			) : isError || !idea ? (
				<ErrorState>Could not load this idea.</ErrorState>
			) : (
				<>
					<DetailHeader>
						<DetailTitle>{idea.name}</DetailTitle>
						{idea.isDeleted && <DeletedBadge>Deleted</DeletedBadge>}
					</DetailHeader>

					<Card>
						<CardTitle>Idea</CardTitle>
						<FieldGrid>
							<Field>
								<FieldLabel>Scope</FieldLabel>
								<FieldValue>{idea.scope === 'trip' ? 'Trip idea' : 'Personal idea'}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Trip</FieldLabel>
								<FieldValue>
									{idea.scope === 'trip' && idea.tripId != null ? (
										<InlineLink to={`/admin/trips/${idea.tripId}`}>
											{idea.tripName ?? 'View trip'}
										</InlineLink>
									) : (
										'—'
									)}
								</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Category</FieldLabel>
								<FieldValue>{idea.category}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Location</FieldLabel>
								<FieldValue>{idea.locationName ?? '—'}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Created by</FieldLabel>
								<FieldValue>
									<InlineLink to={`/admin/users/${idea.createdBy.id}`}>
										{formatName(idea.createdBy.firstName, idea.createdBy.lastName)}
									</InlineLink>
								</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Created</FieldLabel>
								<FieldValue>{formatDateTime(idea.createdAt)}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Last updated</FieldLabel>
								<FieldValue>{formatDateTime(idea.lastUpdatedAt)}</FieldValue>
							</Field>
						</FieldGrid>
					</Card>

					{idea.description && (
						<Card>
							<CardTitle>Description</CardTitle>
							<FieldValue>{idea.description}</FieldValue>
						</Card>
					)}

					<Card>
						<CardTitle>Tags ({idea.tags.length})</CardTitle>
						{idea.tags.length === 0 ? (
							<EmptyState>No tags.</EmptyState>
						) : (
							<Chips>
								{idea.tags.map(tag => (
									<Tag key={tag}>{tag}</Tag>
								))}
							</Chips>
						)}
					</Card>
				</>
			)}
		</>
	)
}

export default IdeaDetailPage
